import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  departmentService,
  NODE_TYPES,
  readApiError,
} from "../../services/departmentService";
import { TYPE_WORDS } from "./ui";

const BLUE = { 600: "#0c8a9b", 700: "#0a7480", 900: "#0f172a" };
const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Each rung down the tree usually narrows in the same way, so the next type is
// offered by default. It stays a plain select because real org charts skip
// rungs constantly and nothing here should stop them.
const NEXT_TYPE = {
  dept: "team",
  div: "team",
  team: "role",
  role: "grade",
  grade: "grade",
};

const NodeFormDialog = ({
  open,
  onClose,
  mode, // "create" | "edit"
  departmentId,
  parentNode, // create only: the level this sits under, null for a top level
  node, // edit only
  breadcrumb = [],
  store_id,
  user_id,
  onSaved,
}) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: "",
    node_type: "team",
    headcount_planned: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSaving(false);
    if (isEdit && node) {
      setForm({
        name: node.name || "",
        node_type: node.node_type || node.type || "team",
        headcount_planned: node.headcount_planned ?? "",
      });
    } else {
      setForm({
        name: "",
        node_type: parentNode
          ? NEXT_TYPE[parentNode.node_type || parentNode.type] || "team"
          : "team",
        headcount_planned: "",
      });
    }
  }, [open, isEdit, node, parentNode]);

  const setField = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Give it a name first");
      return;
    }
    setSaving(true);
    setError("");

    const payload = {
      store_id,
      department_id: departmentId,
      name: form.name.trim(),
      node_type: form.node_type,
      headcount_planned:
        form.headcount_planned === "" ? null : Number(form.headcount_planned),
    };

    try {
      if (isEdit) {
        await departmentService.updateNode(node.id, payload);
      } else {
        // level_no and path are derived server-side from parent_id, so a stale
        // tree in another tab cannot write a wrong depth.
        await departmentService.createNode({
          ...payload,
          parent_id: parentNode?.id || null,
          created_by: user_id,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(readApiError(err, "Could not save that. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const trail = breadcrumb.map((item) => item.name).join(" › ");

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { sx: { borderRadius: "14px" } } }}
    >
      <DialogTitle
        sx={{ fontFamily: fontFam, fontWeight: 700, color: BLUE[900], pb: 0.5 }}
      >
        {isEdit
          ? `Rename “${node?.name || ""}”`
          : parentNode
          ? `Add inside ${parentNode.name}`
          : "Add a top-level division or team"}
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "12px !important" }}
      >
        {trail && (
          <Typography
            sx={{
              fontFamily: fontFam,
              fontSize: 12.5,
              color: "#64748b",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              px: 1.25,
              py: 0.75,
            }}
          >
            {trail}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ fontFamily: fontFam }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Name"
          value={form.name}
          onChange={setField("name")}
          fullWidth
          required
          autoFocus
          placeholder={parentNode ? "Senior Frontend Engineer" : "Engineering"}
          helperText={
            parentNode
              ? "Use the name people would recognise — a team, or the job title you hire for."
              : "The top tier of this department, e.g. a division or a large team."
          }
          InputLabelProps={{ sx: { fontFamily: fontFam } }}
          inputProps={{ style: { fontFamily: fontFam } }}
          FormHelperTextProps={{ sx: { fontFamily: fontFam } }}
        />

        <TextField
          label="What is it?"
          value={form.node_type}
          onChange={setField("node_type")}
          select
          fullWidth
          helperText="Just a label so the structure reads clearly. It does not limit what you can put inside."
          InputLabelProps={{ sx: { fontFamily: fontFam } }}
          FormHelperTextProps={{ sx: { fontFamily: fontFam } }}
        >
          {NODE_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              <Box>
                <Typography sx={{ fontFamily: fontFam, fontSize: 14 }}>
                  {type.label}
                </Typography>
                <Typography
                  sx={{ fontFamily: fontFam, fontSize: 11.5, color: "#94a3b8" }}
                >
                  {TYPE_WORDS[type.value]?.hint}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Planned headcount (optional)"
          value={form.headcount_planned}
          onChange={setField("headcount_planned")}
          type="number"
          fullWidth
          helperText="How many people you intend to have here. Used for reporting only."
          InputLabelProps={{ sx: { fontFamily: fontFam } }}
          FormHelperTextProps={{ sx: { fontFamily: fontFam } }}
          inputProps={{ min: 0, style: { fontFamily: fontFam } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ fontFamily: fontFam, color: "#64748b", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{
            fontFamily: fontFam,
            textTransform: "none",
            fontWeight: 600,
            bgcolor: BLUE[600],
            "&:hover": { bgcolor: BLUE[700] },
          }}
        >
          {isEdit ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeFormDialog;
